"use client";

import { useEffect } from 'react';

export function ExternalScriptErrorHandler() {
  useEffect(() => {
    // Store original console methods
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    
    // Function to detect external script errors
    const isExternalScriptError = (message: string, stack: string = ''): boolean => {
      const externalErrorPatterns = [
        'enabledFeatures',
        'args.site',
        'isFeatureBroken',
        'updateFeaturesInner',
        'chrome-extension://',
        'moz-extension://',
        'safari-extension://',
        'extension://',
        'browser-extension://',
        'web-accessible-resources',
        'content_script',
        'background_script',
        'injected_script',
        'anonymous code',
        'overrideMethod',
        'geckoformboundary',
        'firefox',
        'browser extension',
        'third-party script',
        'external script',
        'manifest.json',
        'content_security_policy',
        'cross-origin',
        'cors',
        'x-frame-options',
        'Failed to fetch RSC payload',
        'NetworkError when attempting to fetch resource',
        'hmrRefresh',
        'fetchServerResponse',
        'router-reducer',
        "can't access property \"includes\", args.site.enabledFeatures is undefined"
      ];
      
      const combinedText = `${message} ${stack}`.toLowerCase();
      
      return externalErrorPatterns.some(pattern => 
        combinedText.includes(pattern.toLowerCase())
      );
    };
    
    // Handle uncaught exceptions
    const handleError = (event: ErrorEvent) => {
      const message = event.message || '';
      const stack = event.error?.stack || '';
      
      if (isExternalScriptError(message, stack)) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    
    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason: any = event.reason;
        const message = typeof reason === 'string' ? reason : reason?.message || '';
        const stack = reason?.stack || '';
        
        if (isExternalScriptError(message, stack)) {
          event.preventDefault();
          return;
        }
      } catch {
        // Ignore errors in error handling
      }
    };
    
    // Override console methods to filter external script errors
    console.error = (...args: any[]) => {
      const message = args.join(' ');
      if (isExternalScriptError(message)) {
        return;
      }
      originalConsoleError.apply(console, args);
    };
    
    console.warn = (...args: any[]) => {
      const message = args.join(' ');
      if (isExternalScriptError(message)) {
        return;
      }
      originalConsoleWarn.apply(console, args);
    };
    
    // Add global error listeners
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleRejection);

    // Cleanup
    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
